import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconWifiOff } from '#src/atoms/icons/system/iconWifiOff.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Popup } from '#src/atoms/layout/Popup/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS } from '#src/atoms/typography/index.tsx';
import { API_ROOT } from '#src/config.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';

const Wrapper = styled(Horizontal).attrs({ gap: 2 })`
  color: var(--c-white);
  padding: var(--double-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-200);
  background: var(--c-nd-100);
`;

const OfflineIcon = styled.div`
  opacity: 0.7;
`;

const Message = styled(BodyS)`
  flex: 1;
`;

const CloseButton = styled.div`
  align-self: flex-start;
`;

const PROBE_TIMEOUT_MS = 4_000;
const RECHECK_INTERVAL_MS = 5_000;

// `navigator.onLine` / offline events reflect the OS network-validation
// verdict, which under a VPN or inside a WebView can report offline while
// requests to api.sorare.com still succeed. A real request is the source of
// truth. `no-cors` keeps the response opaque but it still resolves when the
// host is reachable and rejects on a genuine network failure — enough here.
const canReachApi = async (): Promise<boolean> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(`${API_ROOT}/?_=${Date.now()}`, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

export const NetworkInfo = () => {
  const [offline, setIsOffline] = useState(false);
  const [shouldBeClosed, setShouldBeClosed] = useState(false);

  const { isMobileApp } = useIsMobileApp();

  // Guards against a stale async probe flipping state after a newer event.
  const probeIdRef = useRef(0);

  const verifyOffline = useCallback(async () => {
    const probeId = (probeIdRef.current += 1);
    const reachable = await canReachApi();
    if (probeId !== probeIdRef.current) return;
    setIsOffline(!reachable);
  }, []);

  useEffect(() => {
    function onlineHandler() {
      probeIdRef.current += 1;
      setIsOffline(false);
    }

    function offlineHandler() {
      verifyOffline();
    }

    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, [verifyOffline]);

  // While shown, keep probing so the banner clears once the API is reachable
  // again, regardless of whether the browser emits an `online` event.
  useEffect(() => {
    if (!offline) return undefined;
    const interval = setInterval(verifyOffline, RECHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [offline, verifyOffline]);

  if (isMobileApp) {
    return null;
  }

  if (!offline) {
    if (shouldBeClosed) {
      setShouldBeClosed(false);
    }
    return null;
  }

  if (shouldBeClosed) {
    return null;
  }

  return (
    <Popup>
      <Wrapper>
        <OfflineIcon>
          <FontAwesomeIcon icon={iconWifiOff} />
        </OfflineIcon>
        <Message>
          <FormattedMessage defaultMessage="You're offline. Most actions might not work" />
        </Message>
        <CloseButton>
          <IconButton
            color="transparent"
            size="small"
            icon={iconX}
            onClick={() => {
              setShouldBeClosed(true);
            }}
          />
        </CloseButton>
      </Wrapper>
    </Popup>
  );
};
